import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { User } from '@fruitways/shared';
import { api } from '@/api/endpoints';
import { NetworkError, onSessionChange, refreshSession, setAccessToken } from '@/api/client';
import { KV, clearAll, getStoredUser, setKv } from '@/db/database';
import { pullFromServer } from '@/db/sync';

type Phase = 'booting' | 'signedOut' | 'signedIn';

interface AuthState {
  phase: Phase;
  user: User | null;
  login: (username: string, password: string, remember: boolean) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

/**
 * Offline-first session. A stored user keeps the app usable with cached data even when the
 * access token cannot be refreshed for lack of signal. Only an explicit refusal from the
 * server (refresh cookie gone or revoked) signs the grower out.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>('booting');
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = await getStoredUser();
      if (cancelled) return;
      if (!stored) {
        setPhase('signedOut');
        return;
      }
      setUser(stored);
      setPhase('signedIn');
      try {
        const ok = await refreshSession();
        if (ok && !cancelled) {
          void pullFromServer().catch(() => undefined);
        }
      } catch (err) {
        if (!(err instanceof NetworkError)) throw err;
        // No signal: stay signed in on cached data.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(
    () =>
      onSessionChange((signedIn) => {
        if (!signedIn) {
          void clearAll();
          setUser(null);
          setPhase('signedOut');
        }
      }),
    [],
  );

  const login = useCallback(async (username: string, password: string, remember: boolean) => {
    const res = await api.auth.login({ username, password, remember });
    await setKv(KV.user, res.user);
    setUser(res.user);
    setPhase('signedIn');
    await pullFromServer().catch(() => undefined);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      // Signing out locally is what matters; the cookie will expire on its own.
    }
    setAccessToken(null);
    await clearAll();
    setUser(null);
    setPhase('signedOut');
  }, []);

  const value = useMemo(() => ({ phase, user, login, logout }), [phase, user, login, logout]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
